import sileo from 'sileo';
import axios from 'source/lib/axios';
import eventBus from 'source/lib/event-bus';

/*
 * Concerns related models
 **/
const CardChannel = new sileo.Model('hqzen', 'card-channel');
const CardMapping = new sileo.Model('hqzen', 'card-mapping');
const CommandBoards = new sileo.Model('board', 'command-board-display');
const ConcernAttachmentAmazonFormData = new sileo.Model('hqzen', 'concern-attachment-amazon-form-data');
const ConcernAttachment = new sileo.Model('hqzen', 'concern-attachment');
const EngagementFormDocumentResource = new sileo.Model('hqzen', 'engagement-form-document-resource');
const ForwardableBoards = new sileo.Model('board', 'forwardable-board-display');
const InternalBposeatsBoard = new sileo.Model('hqzen', 'internal-bposeats-board');
const InternalCard = new sileo.Model('hqzen', 'internal-card');
const CommandBoardsChatbots = new sileo.Model('board', 'command-board-chatbots-display');
const CommandBoardInternalCard = new sileo.Model('hqzen', 'command-board-chatbot-internal-card');
const FormEmailConfig = new sileo.Model('hqzen', 'engagement-form-email-config');
const MessageTemplateImageAmazonFormData = new sileo.Model('marketing', 'image-upload');
const CustomSchemeImageAmazonFormData = new sileo.Model('board', 'custom-scheme-image-amazon-form-data');

function websocketOnBoardChatbotMessageHandler(message) {
    let { payload } = JSON.parse(message.data.payload);
    eventBus.$emit('board-message-streamed', payload);
}

function initializeWorker() {
    return new Worker(new URL('./worker.js', import.meta.url));
  }

const state = {
    webWorkerConnection: {}
};

const getters = {

};

const mutations = {
    terminateWebWorkerConnection(state) {
        const token = localStorage.getItem('command_board_token');
        if (state.webWorkerConnection[token]) {
          state.webWorkerConnection[token].postMessage({ action: 'terminateConnection' });
          state.webWorkerConnection[token].terminate();
          state.webWorkerConnection[token] = null;
        }
    },
};

const actions = {
    async initiateCommandBoardChatbotWebsocketConnection({ commit, state }, token) {
        const commandBoardToken = localStorage.getItem('command_board_token');

        if (commandBoardToken && state.webWorkerConnection[commandBoardToken])
          return;

        let data = await axios.get('/websocket-boards-chatbot-token/?token=' + token);
        token = data.token;
        localStorage.setItem('command_board_token', token);

        if (!state.webWorkerConnection[token]) {
          state.webWorkerConnection[token] = initializeWorker();
          state.webWorkerConnection[token].postMessage({
            action: 'initiateConnection',
            payload: {
                token: token,

                API_URL: process.env.API_URL,
                /* eslint-disable */
            }
          });
        }

        state.webWorkerConnection[token].onmessage = function(event) {
          if (event.data.type === 'websocketMessage') {
            websocketOnBoardChatbotMessageHandler(event);
          } else if (event.data.type === 'websocketClosed') {
            commit('terminateWebWorkerConnection');
          }
        };

        state.webWorkerConnection[token].onerror = function(event) {
          commit('terminateWebWorkerConnection');
          console.error('Worker Error:', event);
        };
      },

    fetchAIResponse(context, payload) {
        const commandBoardToken = localStorage.getItem('command_board_token');

        if (commandBoardToken) {
          payload['auth_token'] = localStorage.getItem('command_board_token');
        }
        const form = new FormData();
        Object.keys(payload).forEach((key) => {
          form.append(key, payload[key]);
        });
        return axios.post('board/ai-completion', form);
    },

    createCardChannel(context, data) {
        let form = new FormData();
        for (let item in data) {
            form.append(item, data[item]);
        }

        return CardChannel.objects.create(form);
    },

    createCardMapping(context, data) {
        let dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            if (key === 'fields') {
                data['fields'] = JSON.stringify(data.fields);
            }
        });

        return CardMapping.objects.create(data);
    },

    createConcern(context, data) {
        let form = new FormData();
        for (let item in data) {
            form.append(item, data[item]);
        }

        return InternalCard.objects.create(form);
    },

    createConcernAttachment(context, data) {
        let form = new FormData();

        for (let item in data) {
            form.append(item, data[item]);
        }

        return ConcernAttachment.objects.create(form);
    },

    createEngagementForm(context, data) {
        let form = new FormData();
        for (let field in data) {
            if (field === 'sections' || field === 'user_field_questions') {
                data[field] = JSON.stringify(data[field]);
            }
            form.append(field, data[field]);
        }
        return EngagementFormDocumentResource.objects.create(form);
    },

    archiveEngagementForm(_, pk) {
        return EngagementFormDocumentResource.objects.delete({ pk });
    },

    createCommandBoardCard(context, data){
        let form = new FormData();
        for (let item in data){
            form.append(item, data[item]);
        }
        return CommandBoardInternalCard.objects.create(form);
    },

    deleteCardMapping(context, filters) {
        return CardMapping.objects.delete(filters);
    },

    fetchCardMapping(context, filters) {
        return CardMapping.objects.filter(filters);
    },

    fetchCommandBoards(context, filters) {
        return CommandBoards.objects.filter(filters);
    },

    fetchEngagementForms(context, filters) {
        return EngagementFormDocumentResource.objects.filter(filters);
    },

    fetchForwardableBoards(context, filters) {
        return ForwardableBoards.objects.filter(filters);
    },

    fetchInternalBposeatsBoard(context, filters) {
        return InternalBposeatsBoard.objects.filter(filters);
    },

    async fetchConcernAttachmentAmazonFormData(context, filters) {
        return await ConcernAttachmentAmazonFormData.objects.filter(
            filters).then(amazon => {
                return amazon.amazon;
            });
    },

    async fetchMessageTemplateImageAmazonFormData(context, filters) {
        return await MessageTemplateImageAmazonFormData.objects.filter(filters).then(amazon => {
            return amazon.amazon;
        });
    },

    fetchFormEmailConfigs(context, filters) {
        return FormEmailConfig.objects.filter(filters);
    },

    createFormEmailConfig(context, data) {
        const form = new FormData();

        for (let field in data) {
            form.append(field, data[field]);
        }

        return FormEmailConfig.objects.create(form);
    },

    updateFormEmailConfig(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }

        return FormEmailConfig.objects.update({ 'pk': pk }, form);
    },

    deleteFormEmailConfig(context, data) {
        return FormEmailConfig.objects.delete(data);
    },

    renderDefaultFormTemplate(_, data) {
        const form = new FormData();

        Object.keys(data).forEach((key) => {
          form.append(key, data[key]);
        });

        return axios.post('boards/default-form-template/', form);
    },

    getEngagementForm(context, id) {
        return EngagementFormDocumentResource.objects.get(id);
    },

    updateCardChannel(context, data) {
        const form = new FormData();
        let pk = data.pk;

        for (let field in data) {
            form.append(field, data[field]);
        }

        return CardChannel.objects.update({ 'pk': pk }, form);
    },

    updateCardMapping(context, data) {
        return CardMapping.objects.update({ 'pk': data.pk }, data);
    },


    updateEngagementForm(context, data) {
        const form = new FormData();

        let pk = data.pk, dataKeys = Object.keys(data);

        dataKeys.forEach(key => {
            if (key === 'sections' || key === 'user_field_questions') {
                data[key] = JSON.stringify(data[key]);
            }
            form.append(key, data[key]);
        });
        return EngagementFormDocumentResource.objects.update( { 'pk': pk } , form);
    },

    fetchCommandBoardsChatbots(context, filters) {
        return CommandBoardsChatbots.objects.filter(filters);
    },

    saveCommandBoardChatbotsConversation(context, payload) {
        const form = new FormData();
        Object.keys(payload).forEach((key) => {
          form.append(key, payload[key]);
        });
        return axios.post('board/command-board-chatbot-conversation', form);
    },

    async fetchCustomSchemeImageAmazonFormData(context, filters) {
        return await CustomSchemeImageAmazonFormData.objects.filter(filters).then(amazon => {
            return amazon.amazon;
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
